import { FC, Fragment, useMemo, useRef } from 'react'

import { BodyContainer, Grid } from './parts/About'
import {
  RecordContainer,
  RecordTitle,
  TablesWrapper,
  TableTitle,
  TableWrapper,
} from './parts'
import { IPool } from '../../types/reducerTypes'

import { showDecimalsIfNeeded } from '../Dashboard/Allocations/InvestmentInfo'
import { numberToLocaleString } from '../../utils/numbers'
import { wrapEthOrUsdb } from '../Home/useUpcomingProjects'

import { getExchangeRatePrice, getExchangeRatePriceRange } from './SwapBox'

const oneDayInSeconds = 60 * 60 * 24
const oneWeekInSeconds = oneDayInSeconds * 7
const oneMonthInSeconds = oneDayInSeconds * 28

export const VESTING_PERIODS = [
  { label: 'Instant Vesting', value: '0' },
  { label: 'Daily', value: String(oneDayInSeconds) },
  {
    label: 'Weekly',
    value: String(oneWeekInSeconds),
  },
  { label: 'Monthly', value: String(oneMonthInSeconds) },
]

const Record: FC<{
  title: string
  value: string | number | boolean
}> = ({ title, value }) => {
  return (
    <RecordContainer
      className="flex flex-row relative w-full"
      key={value + title}
    >
      <RecordTitle>{title}</RecordTitle>
      <p>{value}</p>
    </RecordContainer>
  )
}

export const formatDateString = (date: string, showTime = true) => {
  const offset = new Date().getTimezoneOffset() / 60
  const dateObj = new Date(date)
  dateObj.setHours(dateObj.getHours() + offset)
  const dateString = dateObj.toLocaleDateString()
  const time = `${date.split('T')[1].split(':').splice(0, 2).join(':')} UTC`
  return `${dateString}${showTime ? ` ${time}` : ''}`
}

interface Props {
  pool: IPool
}

const Details: FC<Props> = ({ pool }) => {
  const gridRef = useRef<HTMLDivElement>(null)

  const isLabsPool = !!pool?.isVetted
  const slug = pool?.slug ?? ''
  const showTBD =
    isLabsPool || slug.includes('degenwars') || slug.includes('seismic')

  const {
    burnUnsold,
    totalSupplyForSale,
    tgePrice,
    tgePriceRange,
    totalSupplyOfToken,
    totalTokensForLiquidity,
    totalPercentageOfFundsRaisedForLiquidity,
    raiseCurrency,
    vestingSettings,
    liquidityPoolGenerationTime,
    lpLockInPeriodUnit,
    lpLockInPeriodAmount,
  } = pool?.poolSettings ?? {}

  const calculatedMcap = useMemo(() => {
    if (!totalSupplyOfToken || !tgePrice) return 0
    return totalSupplyOfToken * tgePrice
  }, [tgePrice, totalSupplyOfToken])
  const calculatedCirculatingMcap = useMemo(() => {
    if (!totalSupplyForSale || !tgePrice || !totalTokensForLiquidity) return 0
    return (totalSupplyForSale + totalTokensForLiquidity) * tgePrice
  }, [tgePrice, totalSupplyForSale, totalTokensForLiquidity])

  const lpLockInPeriodInSeconds =
    lpLockInPeriodUnit === 'minutes'
      ? lpLockInPeriodAmount * 60
      : lpLockInPeriodUnit === 'months'
      ? lpLockInPeriodAmount * 60 * 60 * 24 * 30
      : lpLockInPeriodAmount * 60 * 60 * 24 * 365
  const liquidityUnlockedAt =
    liquidityPoolGenerationTime &&
    new Date(liquidityPoolGenerationTime).getTime() +
      lpLockInPeriodInSeconds * 1000

  const isEthRaise = raiseCurrency === 'eth'

  const {
    firstVestPercentage,
    vestingPeriodStart,
    vestingInvervalInSeconds,
    numberOfVestingPoints,
    differentInitialVestingValue,
  } = vestingSettings ?? {}

  const periodName =
    VESTING_PERIODS.find((period) => period.value === vestingInvervalInSeconds)
      ?.label ?? 'Monthly'

  const subsequentUnlocks = showDecimalsIfNeeded(
    (100 - Number(differentInitialVestingValue ? firstVestPercentage : 0)) /
      Number(
        differentInitialVestingValue
          ? numberOfVestingPoints
          : numberOfVestingPoints,
      ),
  )

  const FIVE_MINUTES_IN_MS = 5 * 60 * 1000
  const vestingPeriodtStartInMs = new Date(vestingPeriodStart ?? 0).getTime()

  const isInstantVesting = numberOfVestingPoints === 1

  const infoFields = [
    {
      label: 'Total Vesting Points',
      value: numberOfVestingPoints,
    },
    {
      label: 'First Vesting Point',
      value: formatDateString(
        new Date(vestingPeriodtStartInMs + FIVE_MINUTES_IN_MS).toISOString(),
      ),
    },
    {
      label: 'Vesting Interval',
      value: periodName,
      shouldHide: isInstantVesting,
    },
    {
      label:
        (!differentInitialVestingValue ? '' : 'First ') + 'Vesting Unlocks',
      value: `${firstVestPercentage}%`,
      shouldHide: isInstantVesting,
    },
    {
      label: 'Subsequent Unlocks',
      value: `${subsequentUnlocks}%`,
      shouldHide: !differentInitialVestingValue || isInstantVesting,
    },
  ]
  const VestingTable = () => (
    <>
      {infoFields.map(({ label, value, shouldHide }, index) =>
        shouldHide ? (
          <Fragment key={index} />
        ) : (
          <Record key={index} title={label} value={value} />
        ),
      )}
    </>
  )

  const PoolInformation = () => {
    return (
      <>
        {!!pool?.privateSale?.price && (
          <Record
            title="raise price"
            value={
              showTBD
                ? 'TBD'
                : getExchangeRatePrice({
                    ticker: pool?.ticker,
                    price: pool?.privateSale?.price,
                    poolSettings: pool?.poolSettings,
                  })
            }
          />
        )}
        {(!!tgePrice || !!tgePriceRange) && (
          <Record
            title="tge price"
            value={
              showTBD
                ? 'TBD'
                : tgePriceRange
                ? getExchangeRatePriceRange({
                    ticker: pool?.ticker,
                    priceRange: tgePriceRange,
                    poolSettings: pool?.poolSettings,
                  })
                : getExchangeRatePrice({
                    ticker: pool?.ticker,
                    price: String(tgePrice),
                    poolSettings: pool?.poolSettings,
                  })
            }
          />
        )}
        <Record
          title="raise Start"
          value={
            showTBD
              ? 'TBD'
              : pool?.privateSale?.start
              ? formatDateString(pool?.privateSale.start)
              : 'TBC'
          }
        />
        <Record
          title="raise End"
          value={
            showTBD
              ? 'TBD'
              : pool?.privateSale?.end
              ? formatDateString(pool?.privateSale.end)
              : 'TBC'
          }
        />
        {!!totalSupplyOfToken && (
          <Record
            title="Total token supply"
            value={showTBD ? 'TBD' : numberToLocaleString(totalSupplyOfToken)}
          />
        )}
        {!!totalSupplyForSale && (
          <Record
            title="Total token supply for sale"
            value={showTBD ? 'TBD' : numberToLocaleString(totalSupplyForSale)}
          />
        )}
        {!isLabsPool && (
          <>
            {!!totalTokensForLiquidity && (
              <Record
                title="Total tokens for liquidity"
                value={numberToLocaleString(totalTokensForLiquidity)}
              />
            )}
            {!!totalPercentageOfFundsRaisedForLiquidity && (
              <Record
                title={`Total ${(
                  raiseCurrency ?? 'usdb'
                )?.toUpperCase()} % in liquidity pool`}
                value={numberToLocaleString(
                  totalPercentageOfFundsRaisedForLiquidity,
                )}
              />
            )}
            {!!calculatedCirculatingMcap && (
              <Record
                title="Launch Mcap"
                value={wrapEthOrUsdb(calculatedCirculatingMcap, isEthRaise)}
              />
            )}
            {!!calculatedMcap && (
              <Record
                title="FDV Mcap"
                value={wrapEthOrUsdb(calculatedMcap, isEthRaise)}
              />
            )}
            <Record
              title="Unsold Tokens burned"
              value={burnUnsold ? 'Yes' : 'No'}
            />
            <Record
              title="Liquidity unlocked at"
              value={
                !liquidityUnlockedAt
                  ? ''
                  : formatDateString(
                      new Date(liquidityUnlockedAt).toISOString(),
                    )
              }
            />
          </>
        )}
      </>
    )
  }

  const sections = [
    {
      title: 'Raise Details',
      content: <PoolInformation />,
      ref: useRef<HTMLDivElement>(null),
      shouldHide: false,
    },
    {
      title: 'Vesting schedule',
      content: <VestingTable />,
      ref: useRef<HTMLDivElement>(null),
      shouldHide: isLabsPool,
    },
  ]

  return (
    <Grid ref={gridRef}>
      <BodyContainer>
        <TablesWrapper direction="column">
          {sections
            .filter(({ shouldHide }) => !shouldHide)
            .map(({ title, content, ref }, i) => (
              <Fragment key={`pool-info-table-${i}`}>
                <TableWrapper ref={ref}>
                  <TableTitle>{title}</TableTitle>
                  {content}
                </TableWrapper>
              </Fragment>
            ))}
        </TablesWrapper>
      </BodyContainer>
    </Grid>
  )
}

export default Details
